import firebase from './firebase';
import 'firebase/auth';
// TODO: Firestore does not currently guarantee delivery at all, and may never guarantee once-only delivery
//       https://cloud.google.com/functions/docs/calling/cloud-firestore#limitations_and_guarantees
// import 'firebase/firestore';
import 'firebase/database';

import { InteractionMethod } from './user-interaction';
import { getAuthService } from './AuthService';


const database = firebase.database();

export interface SearchProfile {
    id: string;
    name: string;
    params: {};
    default?: boolean;
}


type SearchProfilesCallback = (profiles?: SearchProfile[]) => void;


const profileSearchConfig: {[searchType: string]: {
    profiles?: SearchProfile[];
    profileCallbacks: SearchProfilesCallback[];
    listeningToProfiles: boolean;
    unsubscribeFirebaseUser?: () => any;
}} = {};

function notifyAll(profileCallbacks: SearchProfilesCallback[], profiles?: SearchProfile[]) {
    for (const cb of profileCallbacks) {
        cb(profiles);
    }
}

function getProfileConfig(searchType: string) {
    let config = profileSearchConfig[searchType];
    if (!config) {
        profileSearchConfig[searchType] = config = {
            profiles: [],
            profileCallbacks: [],
            listeningToProfiles: false
        };
    }
    return config;
}

// @ts-ignore
export function getProfileByName(searchType: string, name?: string | null): SearchProfile | undefined {
    const config = getProfileConfig(searchType);
    let defaultProfile;
    // console.info('    getProfileByName:', name, config);

    if (config.profiles) {
        for (const profile of config.profiles) {
            // console.info('      checking:', profile);
            if (profile.default) {
                if (!name) {
                    // console.info('        this is the default profile');
                    return profile;
                }
                defaultProfile = profile;
            } else if (name && name === profile.name) {
                // console.info('        this profile matches by name');
                return profile;
            }
        }
    }

    // console.info('        return defaultProfile');
    return defaultProfile;
}

/**
 * This can be called multiple times with the same searchType and different callbacks and the listener will only be registered once.
 * The callback will automatically be invoked with `undefined` when the user logs out, and again when the user logs back in.
 *
 * @param searchType
 * @param callback
 * @return a call-back which can be used to unregister the listener
 */
export function loadProfiles(searchType: string, callback: SearchProfilesCallback) {
    const user = getAuthService().user;
    let ref: firebase.database.Reference;
    const config = getProfileConfig(searchType);
    const { profileCallbacks } = config;
    const _notifyAll = notifyAll.bind(null, profileCallbacks);

    if (callback.name !== _notifyAll.name) {
        profileCallbacks.push(callback as SearchProfilesCallback);
    }

    // TODO: speed up loading by caching: localStorage.getItem('search-profiles/' + searchType);

    if (!user) {
        // console.warn('User not logged in, even anonymously');
        callback();
    } else {
        const profilePath = `search-profiles/${user.id}/${searchType}`;
        ref = database.ref(profilePath);  // .orderByChild('name');

        if (config.profiles) {
            // we've already loaded the profiles
            callback(config.profiles);
        } else if (!config.listeningToProfiles) {
            config.listeningToProfiles = true;
            ref.on('value', (snapshot: firebase.database.DataSnapshot) => {
                config.profiles = [];

                snapshot.forEach((profile: firebase.database.DataSnapshot) => {
                    // console.debug('profile:', profile.key, profile.val());
                    if (config.profiles) {
                        // config.profiles[profile.key!] = profile.val();
                        config.profiles.push({id: profile.key, ...profile.val()});
                    }
                    return false;
                });

                if (config.profiles.length === 0) {
                    // console.info('creating default profile', ref);
                    ref.push({params: {}, name: `${searchType}`, default: true});
                }

                _notifyAll(config.profiles);
            });
        }
    }

    const stopListening = function(unsubscribeFirebaseUser?: boolean) {
        config.listeningToProfiles = false;
        if (ref) {
            ref.off('value');
        }
        if (unsubscribeFirebaseUser && config.unsubscribeFirebaseUser) {
            config.unsubscribeFirebaseUser();
            config.unsubscribeFirebaseUser = undefined;
        }
    };

    if (!config.unsubscribeFirebaseUser) {
        config.unsubscribeFirebaseUser = firebase.auth().onAuthStateChanged((newUser?: firebase.User | null) => {
            console.debug('auth state changed, reset profiles for user', newUser && newUser.uid, 'isAnonymous?', newUser && newUser.isAnonymous);
            config.profiles = undefined;
            stopListening();
            loadProfiles(searchType, _notifyAll);
        });
    }

    // Return a callback so that we can clean-up
    return () => {
        profileCallbacks.splice(profileCallbacks.indexOf(callback, 1));
        // console.debug('cleaning up liveSearch', searchType, profileCallbacks.length);
        if (profileCallbacks.length === 0) {
            stopListening(true);
        }
    };
}

export interface SearchStatus {
    submitted?: boolean;
    count?: number;
    totalCount?: number;
}

export type ValueAction<E> = {
    type: 'value';
    payload: {
        id: string;
        data: E;
    }
};
export type ChildAddedAction<E> = {
    type: 'child_added';
    payload: {
        id: string;
        data: E;
        previousSibling?: string | null;
    }
};
export type ChildChangedAction<E> = {
    type: 'child_changed';
    payload: {
        id: string;
        data: E;
        previousSibling?: string | null;
    }
};
export type ChildMovedAction<E> = {
    type: 'child_moved';
    payload: {
        id: string;
        data: E;
        previousSibling?: string | null;
    }
};
export type ChildRemovedAction<E> = {
    type: 'child_removed';
    payload: {
        id: string;
        data: E;
        previousSibling?: string | null;
    }
};
export type StatusAction = {
    type: 'status';
    payload: SearchStatus;
};
export type ErrorAction = {
    type: 'error';
    payload: string;
};
export type LiveSearchAction<E> = ChildAddedAction<E> | ChildChangedAction<E> | ChildMovedAction<E> | ChildRemovedAction<E> | StatusAction | ErrorAction;
export type LiveSearchDispatch<E> = (action: LiveSearchAction<E>) => void;


export type LoadItemAction<E> = ValueAction<E> | StatusAction | ErrorAction;
export type LoadItemDispatch<E> = (action: LoadItemAction<E>) => void;
export function loadItem<E>(searchType: string, id: string, dispatch: LoadItemDispatch<E>) {
    const itemPath = `public-items/${searchType}/${id}`;
    const itemRef = database.ref(itemPath);

    dispatch({type: 'status', payload: {submitted: true}});
    subscribeAndDispatch('value', itemRef, dispatch);

    return () => {
        itemRef.off();
    };
}

export function ignoreItem(searchType: string, id: string, method: InteractionMethod, reason?: string ) {
    const user = getAuthService().user;
    const itemPath = `ignored-items/${user!.id}/${searchType}/${id}`;
    const itemRef = database.ref(itemPath);

    const data = {
        timestamp: Date.now(),
        method
    };
    if (reason) {
        (data as any).reason = reason;
    }

    itemRef.set(data);
}

// @ts-ignore
export function liveSearch<E>(searchType: string, profileId: string, params: {},
                              dispatch: LiveSearchDispatch<E>) {
    const user = getAuthService().user;

    if (!user || !user.id) {
        console.info('User ID not ready, please try search again in a few moments');
        dispatch({type: 'error', payload: 'User ID not ready, please try search again in a few moments'});
    } else {
        console.info('user', user, 'submitting', searchType, 'search');
        dispatch({type: 'status', payload: {submitted: true, count: 0, totalCount: 0}});

        const profilePath = `search-profiles/${user.id}/${searchType}/${profileId}`;
        const resultsPath = `search-results/${user.id}/${profileId}`;

        // listen for the response (the 1st record is 'status')
        const resultsRef = database.ref(resultsPath).orderByPriority(); // .limitToFirst(2);
        // console.info('!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! listening for child_added.......................');

        // resultsRef.on('child_added', snapshot => console.info('child_added!!!!', snapshot));

        database.ref(profilePath).child('params').set(params).then(() => {
            // @ts-ignore
            subscribeAndDispatch('child_added', resultsRef, dispatch, handleStatus);
            subscribeAndDispatch('child_moved', resultsRef, dispatch);
            subscribeAndDispatch('child_removed', resultsRef, dispatch);
            subscribeAndDispatch('child_changed', resultsRef, dispatch, handleStatus);
        });
        return () => {
            // console.info('!!!!!!!!!!!!!!!!!!!!!!!!!!! unlistening');
            resultsRef.off();
        };
    }
}

function subscribeAndDispatch<E>(eventType: 'value',
                                 resultsRef: firebase.database.Reference | firebase.database.Query,
                                 dispatch: LoadItemDispatch<E>): void;
function subscribeAndDispatch<E>(eventType: 'child_added' | 'child_changed' | 'child_moved' | 'child_removed',
                                 resultsRef: firebase.database.Reference | firebase.database.Query,
                                 dispatch: LiveSearchDispatch<E>,
                                 createAction?: (snapshot: firebase.database.DataSnapshot) => LiveSearchAction<E> | undefined): void;
function subscribeAndDispatch<E>(eventType: 'value' | 'child_added' | 'child_changed' | 'child_moved' | 'child_removed',
                                 resultsRef: firebase.database.Reference | firebase.database.Query,
                                 dispatch: (action: {type: any; payload: any}) => void,
                                 createAction?: (snapshot: firebase.database.DataSnapshot) => LiveSearchAction<E> | undefined): void {
    // console.info('subscribe:', eventType, resultsRef);
    // resultsRef.off(eventType);
    // setTimeout(() => {
    resultsRef.on(eventType, (snapshot: firebase.database.DataSnapshot, previousSibling?: string | null) => {
        // console.info(eventType);
        let action = snapshot.key === 'status' && createAction && createAction(snapshot);

        if (!action) {
            const value = snapshot.val();
            // console.info(eventType, snapshot.key, value);
            action = {type: eventType as any, payload: {id: snapshot.key!, data: value, previousSibling}};
        }
        // console.info('dispatch:', action);
        dispatch(action!);
    });
    // }, 100);
}

function handleStatus(snapshot: firebase.database.DataSnapshot): StatusAction | ErrorAction {
    const value = snapshot.val();
    if (value.error) {
        return {type: 'error', payload: value.error};
    } else {
        return {type: 'status', payload: value};
    }
}
